import { PlusCircleIcon, XCircleIcon } from '@heroicons/react/24/outline'
import filter from 'lodash/filter'
import { FieldError, FieldErrorsImpl, Merge } from 'react-hook-form'

import useWindowWidth from '../hooks/useWindowWidth'

import Field from './Field'
import QuantityInput from './inputs/Quantity'
import { Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from './Table'
import TextArea from './TextArea'
import TextInput from './TextInput'

import { PartRequestInput } from '../../types'

const defaultPartRequestInput: PartRequestInput = { key: 0, mpn: '', description: '', quantity: 1 }

const addPart = (partRequestInputs: PartRequestInput[]) => {
  const maxKey = partRequestInputs.reduce(
    (max, partRequestInput) => (partRequestInput.key > max ? partRequestInput.key : max),
    0
  )
  return [...partRequestInputs, { ...defaultPartRequestInput, key: maxKey + 1 }]
}

const removePart = (parts: PartRequestInput[], key: number) =>
  filter(parts, (part: PartRequestInput) => part.key !== key)

const updatePart = (parts: PartRequestInput[], updatedPart: PartRequestInput) =>
  parts.map((part) => (part.key === updatedPart.key ? updatedPart : part))

const PartRequestsInput = ({
  partRequestInputs,
  onChange,
  errors,
  legacyErrors,
}: {
  partRequestInputs: PartRequestInput[]
  onChange: (parts: PartRequestInput[]) => void
  errors?: { mpn: string | null }[]
  legacyErrors?: Merge<
    FieldError,
    (
      | Merge<
          FieldError,
          FieldErrorsImpl<{
            mpn: string
            description: string
            quantity: number
            key: number
          }>
        >
      | undefined
    )[]
  >
}) => {
  const windowWidth = useWindowWidth()

  const mpnError = (index: number) =>
    errors ? errors[index]?.mpn : legacyErrors ? legacyErrors[index]?.mpn?.message : undefined

  return windowWidth < 1280 ? (
    <div className="space-y-6 divide-y">
      {partRequestInputs.map((part, index) => (
        <div
          className={`space-y-4 ${index > 0 ? 'pt-6' : ''}`}
          key={`part-request-input-${part.key}`}
        >
          <div className="flex gap-4">
            <div className="flex-1">
              <Field label="Part number" errors={mpnError(index)}>
                <TextInput
                  id="part-number"
                  value={part.mpn}
                  onChange={(e) =>
                    onChange(updatePart(partRequestInputs, { ...part, mpn: e.target.value }))
                  }
                />
              </Field>
            </div>
            <Field label="Quantity">
              <QuantityInput
                id="quantity"
                min={1}
                value={part.quantity}
                onChange={(e) =>
                  onChange(
                    updatePart(partRequestInputs, { ...part, quantity: e.target.valueAsNumber })
                  )
                }
              />
            </Field>
          </div>
          <Field label="Description">
            <TextArea
              id="description"
              value={part.description}
              onChange={(e) =>
                onChange(updatePart(partRequestInputs, { ...part, description: e.target.value }))
              }
            />
          </Field>
          {partRequestInputs.length > 1 && (
            <div className="flex justify-end">
              <button
                type="button"
                className="flex gap-1 items-center text-gray-400 hover:text-red-700 text-sm"
                onClick={() => {
                  onChange(removePart(partRequestInputs, part.key))
                }}
              >
                <XCircleIcon className="w-6 h-6" />
                <span>Remove part</span>
              </button>
            </div>
          )}
        </div>
      ))}
      <div className="pt-6">
        <button
          type="button"
          className="flex gap-1 items-center text-blue-500 text-sm"
          onClick={() => {
            onChange(addPart(partRequestInputs))
          }}
        >
          <PlusCircleIcon className="w-6 h-6" />
          <span>Add part</span>
        </button>
      </div>
    </div>
  ) : (
    <TableWrapper>
      <Table>
        <Thead>
          <Tr>
            <Th>Part Number</Th>
            <Th>Description</Th>
            <Th>Quantity</Th>
            {partRequestInputs.length > 1 && <Th />}
          </Tr>
        </Thead>
        <Tbody>
          {partRequestInputs.map((part, index) => (
            <Tr key={`part-request-input-${part.key}`}>
              <Td className="w-[17rem]">
                <Field errors={mpnError(index)}>
                  <TextInput
                    value={part.mpn}
                    onChange={(e) =>
                      onChange(updatePart(partRequestInputs, { ...part, mpn: e.target.value }))
                    }
                  />
                </Field>
              </Td>
              <Td>
                <Field>
                  <TextArea
                    className="min-w-48"
                    value={part.description}
                    onChange={(e) =>
                      onChange(
                        updatePart(partRequestInputs, { ...part, description: e.target.value })
                      )
                    }
                  />
                </Field>
              </Td>
              <Td className="w-[8rem]">
                <Field>
                  <QuantityInput
                    min={1}
                    value={part.quantity}
                    onChange={(e) =>
                      onChange(
                        updatePart(partRequestInputs, {
                          ...part,
                          quantity: e.target.valueAsNumber,
                        })
                      )
                    }
                  />
                </Field>
              </Td>
              {partRequestInputs.length > 1 && (
                <Td>
                  <XCircleIcon
                    className="w-6 h-6 text-gray-400 hover:text-red-400 cursor-pointer"
                    onClick={() => onChange(removePart(partRequestInputs, part.key))}
                  />
                </Td>
              )}
            </Tr>
          ))}
          <Tr key="add-new-part">
            <Td>
              <button
                type="button"
                className="flex flex-row items-center justify-start space-x-1 text-blue-500 text-sm"
                onClick={() => {
                  onChange(addPart(partRequestInputs))
                }}
              >
                <PlusCircleIcon className="w-6 h-6" />
                <span>Add part</span>
              </button>
            </Td>
          </Tr>
        </Tbody>
      </Table>
    </TableWrapper>
  )
}

PartRequestsInput.defaultPartRequestInput = defaultPartRequestInput

export default PartRequestsInput
